import { useState, useEffect, useRef, useCallback } from "react";
import Logo from "../../assets/images/logo/linkologo.png";
import PrivateIcon from "../../assets/images/icon/private.png";
import NoDataIcon from "../../assets/images/icon/nodata.png";
import ModalIcon from "../../assets/images/icon/modal.png";
import LinkoappIcon from "../../assets/images/icon/linko-icon-logo.png";
import { useNavigate, useLocation } from "react-router-dom";
import LoginAnimation from "../../components/LoginAnimation";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { toastError, toastSuccess } from "../../components/Toast";
import { setUser, updateUserProfile, setTokenStatus } from "../../store/slices/userSlice";
import { initGlobalSocket } from "../../services/socket";
import AuthService from "../../services/auth";
import UserService from "../../services/user";
import { FaEye, FaEyeSlash, FaEnvelope, FaLock } from "react-icons/fa";
import { MdOutlineGeneratingTokens } from "react-icons/md";
import { useLanguage } from "../../context/LanguageContext";
import localizeAuthError from "../../utils/localizeAuthError";
import { GoogleLogin } from "@react-oauth/google";
import PaymentService from "../../services/payment";
import Flag from "react-flagkit";
import { IoArrowBack } from "react-icons/io5";
import { persistProfileToStorage, readStoredEmail, readStoredPlanName, readStoredGroupName, readStoredIsSubscription } from "../../hooks/useSidebarData";
import ContactUsPopup, { AlreadySentPopup, countryCodes } from "../../components/common/ContactUsPopup";

// Helper function to format token values
const formatTokenValue = (value) => {
  if (!value) return "0";
  const num = Number(value);
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + "M";
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + "K";
  }
  return num.toString();
};

const formatTokenPackCount = (val) => {
  if (val === undefined || val === null || val === "") return "";
  const num = Number(val);
  if (isNaN(num)) return String(val);
  if (num >= 1000000) {
    return num % 1000000 === 0 ? `${num / 1000000}M` : `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return num % 1000 === 0 ? `${num / 1000}K` : `${(num / 1000).toFixed(1)}K`;
  }
  return num.toLocaleString();
};

// Helper function to calculate days remaining
const getDaysRemaining = (expireDate) => {
  if (!expireDate) return 0;
  const now = new Date();
  const expire = new Date(expireDate);
  const diffTime = expire - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
};

const PaymentSuccessPopup = ({ onClose, t }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 12000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative bg-[linear-gradient(157.06deg,#1A0E3E_46.27%,#904DB0_94.25%)] rounded-[27px] border border-[rgba(199,191,229,0.20)] px-8 py-10 flex flex-col items-center gap-6 w-full max-w-[400px] mx-4 shadow-2xl">
        <div className="w-16 h-16 rounded-full bg-[#904DB0]/30 flex items-center justify-center">
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
            <circle cx="18" cy="18" r="18" fill="#904DB0" fillOpacity="0.3" />
            <path d="M10 18l6 6 10-12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div className="text-center">
          <h2 className="text-white font-extrabold text-[24px] leading-tight mb-2">
            {t.paymentSuccessTitle}
          </h2>
          <p className="text-white text-[15px] font-light">{t.paymentSuccessDesc}</p>
        </div>
        <button
          onClick={onClose}
          className="w-full h-[50px] bg-[#904DB0] hover:bg-[#7d3fa0] text-white font-semibold rounded-[87.5px] transition-colors text-[16px]"
        >
          {t.paymentClose}
        </button>
      </div>
    </div>
  );
};

const PaymentFailPopup = ({ onClose, t }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 12000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative bg-[linear-gradient(157.06deg,#1A0E3E_46.27%,#904DB0_94.25%)] rounded-[27px] border border-[rgba(199,191,229,0.20)] px-8 py-10 flex flex-col items-center gap-6 w-full max-w-[400px] mx-4 shadow-2xl">
        <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
            <circle cx="18" cy="18" r="18" fill="#ef4444" fillOpacity="0.25" />
            <path d="M12 12l12 12M24 12l-12 12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        </div>
        <div className="text-center">
          <h2 className="text-white font-extrabold text-[24px] leading-tight mb-2">
            {t.paymentFailTitle}
          </h2>
          <p className="text-[#D2C5E8] text-[15px] font-light">{t.paymentFailDesc}</p>
        </div>
        <button
          onClick={onClose}
          className="w-full h-[50px] bg-[#904DB0] hover:bg-[#7d3fa0] text-white font-semibold rounded-[87.5px] transition-colors text-[16px]"
        >
          {t.paymentTryAgain}
        </button>
      </div>
    </div>
  );
};

// Re-enable payment popup
const ReEnablePaymentPopup = ({ onClose, onConfirm, t, isProcessing }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
    <div className="relative bg-[linear-gradient(157.06deg,#1A0E3E_46.27%,#904DB0_94.25%)] rounded-[27px] border border-[rgba(199,191,229,0.20)] px-8 py-10 flex flex-col items-center gap-6 w-full max-w-[420px] mx-4 shadow-2xl">
      <button
        onClick={onClose}
        disabled={isProcessing}
        className="absolute top-4 right-5 text-white/60 hover:text-white text-2xl leading-none bg-transparent border-none cursor-pointer disabled:opacity-50"
        aria-label="Close"
      >
        ×
      </button>
      <div className="w-16 h-16 rounded-full bg-yellow-500/20 flex items-center justify-center">
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M16 4v12M16 20v4" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="16" cy="28" r="1.5" fill="#fff" />
        </svg>
      </div>
      <div className="text-center">
        <h2 className="text-white font-extrabold text-[22px] mb-2">
          {t.reEnablePaymentTitle || "Re-enable Payment?"}
        </h2>
        <p className="text-[#D2C5E8] text-[14px] font-light leading-relaxed">
          {t.reEnablePaymentDesc || "Your subscription was cancelled. Would you like to re-enable automatic payments? You will be billed on the next renewal date."}
        </p>
      </div>
      <div className="flex gap-3 w-full">
        <button
          onClick={onClose}
          disabled={isProcessing}
          className="flex-1 h-[46px] border border-[#904DB0] text-white font-semibold rounded-[87.5px] transition-colors text-[14px] hover:bg-white/10 disabled:opacity-50"
        >
          {t.reEnablePaymentCancel || "Cancel"}
        </button>
        <button
          onClick={onConfirm}
          disabled={isProcessing}
          className="flex-1 h-[46px] bg-[#904DB0] hover:bg-[#7d3fa0] disabled:opacity-50 text-white font-semibold rounded-[87.5px] transition-colors text-[14px] flex items-center justify-center"
        >
          {isProcessing ? (
            <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            t.reEnablePaymentConfirm || "Yes, Re-enable"
          )}
        </button>
      </div>
    </div>
  </div>
);

// Token Card Component
const TokenCard = ({
  tokenBalance,
  tokenLimit,
  onPurchaseTokens,
  isPurchasingTokens,
  isTokenLoading,
  t,
  language = "en",
  tokenPurchaseCount,
  tokenPlanPrice = "$4.99",
}) => {
  if ((isTokenLoading && (tokenBalance === null || tokenLimit === null)) || (tokenBalance === null && tokenLimit === null)) {
    return (
      <div className="w-full min-w-0 bg-[rgba(199,191,229,0.10)] rounded-[18px] sm:rounded-[22px] border border-[#C7BFE5]/10 p-2 sm:p-2.5 lg:p-2.5 xl:p-3 mt-1 sm:mt-1.5 mb-0.5 animate-pulse">
        <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-2 xl:gap-3">
          <div className="flex-1 min-w-0 w-full">
            <div className="h-3.5 bg-white/20 rounded w-28 mb-1.5" />
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              <div>
                <div className="h-2 bg-white/15 rounded w-10 mb-1" />
                <div className="h-4 bg-white/25 rounded w-14" />
              </div>
              <div>
                <div className="h-2 bg-white/15 rounded w-10 mb-1" />
                <div className="h-4 bg-white/25 rounded w-14" />
              </div>
              <div>
                <div className="h-2 bg-white/15 rounded w-10 mb-1" />
                <div className="h-4 bg-white/25 rounded w-14" />
              </div>
            </div>
            <div className="w-full h-1.5 bg-white/15 rounded-full mt-2" />
          </div>
          <div className="flex flex-col items-center xl:items-end gap-1 w-full xl:w-auto xl:min-w-[140px] shrink-0">
            <div className="w-full sm:w-[130px] h-[32px] xl:h-[35px] bg-white/20 rounded-[87.5px]" />
            <div className="h-2.5 bg-white/15 rounded w-20 mt-0.5" />
          </div>
        </div>
      </div>
    );
  }

  const usedTokens = Math.max(0, (tokenLimit || 0) - (tokenBalance || 0));

  const usagePercentage =
    tokenLimit > 0 ? (usedTokens / tokenLimit) * 100 : 0;

  const remainingPercentage = 100 - usagePercentage;

  return (
    <div className="w-full min-w-0 bg-[rgba(199,191,229,0.10)] rounded-[18px] sm:rounded-[22px] border border-[#C7BFE5]/10 p-2 sm:p-2.5 lg:p-2.5 xl:p-3 mt-1 sm:mt-1.5 mb-0.5 auth-token-card">
      <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-2 xl:gap-3">

        {/* Left Side - Token Information */}
        <div className="flex-1 min-w-0 w-full">
          <h3 className="text-white font-extrabold text-[13px] md:text-[14px] xl:text-[15px] leading-none mb-1">
            {t.tokenBalance || "Token Balance"}
          </h3>

          {/* Token Statistics */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3">

            {/* Available */}
            <div className="flex flex-col min-w-0">
              <span className="text-white/60 text-[8.5px] sm:text-[9.5px] lg:text-[10px] xl:text-[11px] uppercase tracking-wider whitespace-nowrap">
                {t.available || "Available"}
              </span>

              <span className="text-white font-bold text-[13px] sm:text-[14px] lg:text-[15px] xl:text-[17px] 2xl:text-[20px] mt-0.5 whitespace-nowrap">
                {formatTokenValue(tokenBalance || 0)}
              </span>
            </div>

            {/* Used */}
            <div className="flex flex-col min-w-0">
              <span className="text-white/60 text-[8.5px] sm:text-[9.5px] lg:text-[10px] xl:text-[11px] uppercase tracking-wider whitespace-nowrap">
                {t.used || "Used"}
              </span>

              <span className="text-white font-bold text-[13px] sm:text-[14px] lg:text-[15px] xl:text-[17px] 2xl:text-[20px] mt-0.5 whitespace-nowrap">
                {formatTokenValue(usedTokens || 0)}
              </span>
            </div>

            {/* Total */}
            <div className="flex flex-col min-w-0">
              <span className="text-white/60 text-[8.5px] sm:text-[9.5px] lg:text-[10px] xl:text-[11px] uppercase tracking-wider whitespace-nowrap">
                {t.total || "Total"}
              </span>

              <span className="text-white font-bold text-[13px] sm:text-[14px] lg:text-[15px] xl:text-[17px] 2xl:text-[20px] mt-0.5 whitespace-nowrap">
                {formatTokenValue(tokenLimit || 1000)}
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden mt-1.5 sm:mt-2">
            <div
              className="h-full bg-gradient-to-r from-[#904DB0] to-purple-400 rounded-full transition-all duration-500"
              style={{
                width: `${Math.max(remainingPercentage, 0)}%`,
              }}
            />
          </div>

          {/* Warning */}
          {((tokenLimit > 0 && usagePercentage >= 90) || (tokenBalance !== null && tokenBalance <= 0)) && (
            (() => {
              const isExhausted = (tokenBalance !== null && tokenBalance <= 0) || usagePercentage >= 100;
              return isExhausted ? (
                <div className="mt-1.5 px-2 py-1 rounded-[8px] bg-red-500/20 border border-red-500/40 flex items-center gap-1.5">
                  <p className="text-red-300 text-[10px] sm:text-[11px] font-medium leading-tight">
                    ⚠️ {t.tokenLimitReached || "You have reached your token limit. Please upgrade your plan."}
                  </p>
                </div>
              ) : (
                <p className="text-yellow-300 text-[10px] sm:text-[11px] mt-1 font-medium leading-tight">
                  ⚠️ {t.tokenLimitWarning || "Your token limit is almost reached. Please upgrade your plan."}
                </p>
              );
            })()
          )}
        </div>

        {/* Right Side - Purchase Section */}
        <div className="flex flex-col items-center xl:items-end gap-1 w-full xl:w-auto xl:min-w-[140px] shrink-0">
          <button
            onClick={onPurchaseTokens}
            disabled={isPurchasingTokens}
            className="w-full sm:w-auto min-w-[160px] px-4 sm:px-5 h-[32px] xl:h-[35px] bg-gradient-to-r from-[#904DB0] to-[#6C2DB5] hover:from-[#7d3fa0] hover:to-[#5a2499] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold rounded-[87.5px] transition-all duration-300 flex items-center justify-center gap-1.5 text-[11.5px] xl:text-[12.5px] whitespace-nowrap shadow-[0_4px_15px_rgba(144,77,176,0.4)] hover:shadow-[0_6px_20px_rgba(144,77,176,0.55)] active:scale-[0.97]"
          >
            {isPurchasingTokens ? (
              <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <MdOutlineGeneratingTokens size={16} />

                {t.purchaseTokens || "Purchase Tokens"}
              </>
            )}
          </button>

          <span className="text-[#C9A0E2] text-[10.5px] xl:text-[11px] font-medium text-center xl:text-right">
            {tokenPurchaseCount
              ? (language === "es"
                ? `${tokenPlanPrice} por ${formatTokenPackCount(tokenPurchaseCount)} tokens`
                : `${tokenPlanPrice} per ${formatTokenPackCount(tokenPurchaseCount)} tokens`)
              : (t.perTokenPack || "$4.99 per token pack")}
          </span>
        </div>
      </div>
    </div>
  );
};

// Plan Card Component
const PlanCard = ({
  title,
  price,
  bullets,
  subscribeLabel,
  onSubscribe,
  isLoading,
  isActive,
  onCancel,
  isCancelling,
  trialMessage,
  isCancelPending,
  showReEnable = false,
  onReEnable,
  isReEnabling,
  hideSubscribe = false,
  rejectionNote,
  t,
  isOrgsPlanActive = false,
  isLoggedIn = false,
  isCompact = false,
}) => {
  const isActionsDisabled = isOrgsPlanActive || (isActive && !isCancelPending);
  const isOrgs = (title || "").toLowerCase().includes("org") || (title || "").toLowerCase().includes("organiza");
  const displayTitle = isOrgs ? (t?.orgs || "ORGANIZATIONS").toUpperCase() : (title || "PROFESSIONALS").toUpperCase();
  const hasCancelButton = isActive && !isCancelPending && Boolean(onCancel) && !isOrgsPlanActive;
  const hasBothButtons = !hideSubscribe && hasCancelButton;

  return (
    <div className={`relative w-full flex-1 h-full ${isCompact
      ? 'min-h-[300px] sm:min-h-[320px] lg:h-full xl:h-[360px] 2xl:h-[410px] p-2.5 sm:p-3 lg:p-3 xl:p-3.5 2xl:p-4.5'
      : 'min-h-[340px] sm:min-h-[360px] lg:h-[385px] xl:h-[405px] 2xl:h-[465px] p-3.5 sm:p-4 lg:p-4 xl:p-4.5 2xl:p-5.5'
      } bg-[rgba(199,191,229,0.08)] backdrop-blur-sm rounded-[26px] border ${isOrgs ? 'border-[#00D2D3]/40 shadow-[0_0_20px_rgba(0,210,211,0.15)]' : 'border-[#904DB0]/40 shadow-[0_0_20px_rgba(144,77,176,0.15)]'
      } flex flex-col justify-between transition-all hover:scale-[1.005] auth-plan-card ${hasBothButtons ? 'has-both-buttons' : ''}`}>
      {isActive && (
        <span className={`absolute ${isCompact ? 'top-2 right-2 text-[8px] sm:text-[8.5px] px-2 py-0.5' : 'top-3.5 right-3.5 text-[10px] sm:text-[11px] px-2.5 py-0.5'} ${isOrgs ? 'bg-[#00D2D3] text-[#1A0E3E]' : 'bg-[#904DB0] text-white'} font-black rounded-full uppercase tracking-wider shadow-md z-10`}>
          {isCancelPending ? (t?.cancellingLabel || "Cancelling") : (t?.activeLabel || "Active")}
        </span>
      )}

      {/* Group top section so elements stay tightly aligned without dead space */}
      <div className="flex flex-col w-full flex-1 min-w-0">
        {/* Card Header with Icon Badge & Scalable Title */}
        <div className={`flex items-center gap-2 sm:gap-2.5 ${isCompact ? 'mb-1 sm:mb-1.5' : 'mb-2 sm:mb-2.5'} flex-shrink-0 min-w-0`}>
          <div className={`${isCompact ? 'w-6 h-6 sm:w-7 sm:h-7 lg:w-7 lg:h-7 xl:w-7.5 xl:h-7.5' : 'w-8 h-8 sm:w-8.5 sm:h-8.5 lg:w-8.5 lg:h-8.5 xl:w-9.5 xl:h-9.5 2xl:w-11 2xl:h-11'} rounded-full flex-shrink-0 ${isOrgs ? 'bg-[#00D2D3]/20 border border-[#00D2D3]/60 text-[#00D2D3]' : 'bg-[#904DB0]/30 border border-[#904DB0]/60 text-[#C9A0E2]'} flex items-center justify-center shadow-inner`}>
            {isOrgs ? (
              <svg className={isCompact ? "w-3 h-3 sm:w-3.5 sm:h-3.5 lg:w-3.5 lg:h-3.5" : "w-4 h-4 sm:w-4.5 sm:h-4.5 lg:w-4.5 lg:h-4.5 xl:w-5 xl:h-5"} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5m0 0h5m-5 0V11m0 4h.01M15 11h.01M15 15h.01M9 11h.01M9 15h.01" />
              </svg>
            ) : (
              <svg className={isCompact ? "w-3 h-3 sm:w-3.5 sm:h-3.5 lg:w-3.5 lg:h-3.5" : "w-4 h-4 sm:w-4.5 sm:h-4.5 lg:w-4.5 lg:h-4.5 xl:w-5 xl:h-5"} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            )}
          </div>
          <h3 className={`font-normal flex-1 min-w-0 ${isCompact ? 'text-[11px] sm:text-[12px] lg:text-[12px] xl:text-[13px]' : 'text-[12px] sm:text-[13px] lg:text-[13.5px] xl:text-[15px] 2xl:text-[18px]'} uppercase leading-tight tracking-normal break-words truncate sm:overflow-visible ${isOrgs ? 'text-[#00D2D3]' : 'text-white'}`} title={displayTitle}>
            {displayTitle}
          </h3>
        </div>

        <div className="w-full h-px bg-white/15 my-0.5 sm:my-1 flex-shrink-0" />

        {/* Price Header */}
        <div className={`${isCompact ? 'my-0.5 sm:my-1 min-h-[28px] sm:min-h-[32px] lg:h-[32px] xl:h-[36px]' : 'my-1.5 sm:my-2 min-h-[32px] sm:min-h-[36px] lg:h-[38px] xl:h-[42px] 2xl:h-[46px]'} w-full text-left flex-shrink-0 flex items-center`}>
          {isOrgs ? (
            <h4 className={`text-[#00D2D3] font-normal ${isCompact ? 'text-[18px] sm:text-[22px] lg:text-[22px] xl:text-[26px] 2xl:text-[28px]' : 'text-[20px] sm:text-[24px] lg:text-[26px] xl:text-[30px] 2xl:text-[34px]'} tracking-tight leading-tight whitespace-nowrap overflow-hidden text-ellipsis`}>
              {t?.customPricing || "Custom pricing"}
            </h4>
          ) : price ? (
            <h4 className={`text-[#C9A0E2] font-normal ${isCompact ? 'text-[20px] sm:text-[24px] lg:text-[24px] xl:text-[28px]' : 'text-[22px] sm:text-[28px] lg:text-[30px] xl:text-[34px]'} tracking-tight leading-tight whitespace-nowrap flex items-baseline`}>
              {(() => {
                const str = String(price);
                const hasSlash = str.includes("/");
                const amt = hasSlash ? str.split("/")[0] : str;
                const formattedAmt = amt.trim().startsWith("$") ? amt : `$${amt}`;
                const period = t?.monthAbbr || "mo";
                return (
                  <>
                    <span>{formattedAmt}</span>
                    <span className="text-[0.68em] font-normal text-[#C9A0E2]/90 ml-0.5">/{period}</span>
                  </>
                );
              })()}
            </h4>
          ) : (
            <div className={isCompact ? "h-[24px]" : "h-[28px]"} />
          )}
        </div>

        {/* Feature Bullets - Left Aligned, Vertically Centered with Icons */}
        <ul className={`flex flex-col ${isCompact ? (hasBothButtons ? 'gap-0.5 sm:gap-1 lg:gap-1 xl:gap-1.5 my-0 sm:my-0.5' : 'gap-1 sm:gap-1.5 lg:gap-1.5 xl:gap-2 my-0.5 sm:my-1') : 'gap-2 sm:gap-2.5 lg:gap-2 xl:gap-2.5 2xl:gap-3 my-1.5 sm:my-2'} w-full`}>
          {(bullets || []).map((b, i) => {
            // Individual icons for each bullet point
            const bulletIcons = isOrgs ? [
              // Orgs icons: shield, brain, share, globe
              <svg key="org0" className={isCompact ? "w-3 h-3 sm:w-3.5 sm:h-3.5" : "w-3.5 h-3.5 sm:w-4 sm:h-4"} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
              <svg key="org1" className={isCompact ? "w-3 h-3 sm:w-3.5 sm:h-3.5" : "w-3.5 h-3.5 sm:w-4 sm:h-4"} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>,
              <svg key="org2" className={isCompact ? "w-3 h-3 sm:w-3.5 sm:h-3.5" : "w-3.5 h-3.5 sm:w-4 sm:h-4"} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>,
              <svg key="org3" className={isCompact ? "w-3 h-3 sm:w-3.5 sm:h-3.5" : "w-3.5 h-3.5 sm:w-4 sm:h-4"} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
            ] : [
              // Professional icons: lock-shield, eye-off, pencil-edit, users
              <svg key="pro0" className={isCompact ? "w-3 h-3 sm:w-3.5 sm:h-3.5" : "w-3.5 h-3.5 sm:w-4 sm:h-4"} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
              <svg key="pro1" className={isCompact ? "w-3 h-3 sm:w-3.5 sm:h-3.5" : "w-3.5 h-3.5 sm:w-4 sm:h-4"} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>,
              <svg key="pro2" className={isCompact ? "w-3 h-3 sm:w-3.5 sm:h-3.5" : "w-3.5 h-3.5 sm:w-4 sm:h-4"} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>,
              <svg key="pro3" className={isCompact ? "w-3 h-3 sm:w-3.5 sm:h-3.5" : "w-3.5 h-3.5 sm:w-4 sm:h-4"} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>,
            ];
            const icon = bulletIcons[i % bulletIcons.length];
            return (
              <li key={i} className="flex items-center gap-2 sm:gap-2.5 text-left min-w-0">
                <span className={`${isCompact ? 'w-4.5 h-4.5 sm:w-5 sm:h-5' : 'w-5 h-5 sm:w-6 sm:h-6 lg:w-6 lg:h-6 xl:w-6.5 xl:h-6.5 2xl:w-7 2xl:h-7'} rounded-full ${isOrgs ? 'border border-[#00D2D3] bg-[#00D2D3]/20 text-[#00D2D3]' : 'border border-[#904DB0] bg-[#904DB0]/30 text-[#C9A0E2]'} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                  {icon}
                </span>
                <span className={`text-white ${isCompact ? 'text-[11.5px] sm:text-[12px] lg:text-[11.5px] xl:text-[12.5px]' : 'text-[12.5px] sm:text-[13.5px] lg:text-[13.5px] xl:text-[14.5px] 2xl:text-[15.5px]'} font-normal leading-snug break-words min-w-0`}>
                  {b}
                </span>
              </li>
            );
          })}
        </ul>

        {/* Stable Status Messages Container */}
        <div className={`w-full flex flex-col items-center justify-center ${hasBothButtons ? 'min-h-0 my-0' : 'min-h-[22px] my-0.5'} flex-shrink-0`}>
          {rejectionNote && (
            <p className="text-red-400 text-[10px] sm:text-[11px] text-center font-normal my-0.5 leading-tight break-words px-1" title={`Note: ${rejectionNote}`}>
              Note: {rejectionNote}
            </p>
          )}

          {isOrgsPlanActive && !isOrgs ? (
            <p className="text-yellow-300 text-[10px] sm:text-[11px] text-center font-normal my-0.5 leading-tight">
              {t?.orgsPlanActiveMsg || "ORGs plan active"}
            </p>
          ) : isCancelPending ? (
            <p className="text-[#C9A0E2] text-[10px] sm:text-[11px] text-center font-normal my-0.5 leading-tight">
              {t?.cancellingAtEnd || "Cancelling at end of billing period"}
            </p>
          ) : trialMessage ? (
            <p className="text-[#C9A0E2] text-[10px] sm:text-[11px] font-normal text-center my-0.5 leading-tight">
              {trialMessage}
            </p>
          ) : null}
        </div>
      </div>

      {/* Button at bottom */}
      {!hideSubscribe && (
        isActive && isCancelPending && showReEnable && onReEnable ? (
          <button
            onClick={onReEnable}
            disabled={isLoading || isReEnabling || isOrgsPlanActive}
            className={`w-full ${isCompact ? (hasBothButtons ? 'h-[28px] sm:h-[30px] lg:h-[28px] xl:h-[30px] text-[10.5px] sm:text-[11.5px] mt-1 sm:mt-1.5' : 'h-[32px] sm:h-[34px] lg:h-[32px] xl:h-[34px] text-[11.5px] sm:text-[12.5px] mt-2 sm:mt-2.5') : 'h-[40px] sm:h-[44px] lg:h-[40px] xl:h-[42px] 2xl:h-[46px] text-[11.5px] sm:text-[12.5px] mt-2 sm:mt-2.5'} ${isOrgs ? 'border-2 border-[#00D2D3] text-[#00D2D3] hover:bg-[#00D2D3]/20' : 'bg-[#904DB0] hover:bg-[#7d3fa0] text-white'} disabled:opacity-50 disabled:cursor-not-allowed font-medium rounded-[14px] sm:rounded-[87.5px] transition-all flex items-center justify-center uppercase tracking-wider flex-shrink-0 shadow-md`}
          >
            {isReEnabling ? (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              t?.reEnablePaymentButton || "Re-enable Payment"
            )}
          </button>
        ) : (
          <button
            onClick={onSubscribe}
            disabled={isLoading || isActionsDisabled}
            className={`w-full ${isCompact ? (hasBothButtons ? 'h-[28px] sm:h-[30px] lg:h-[28px] xl:h-[30px] text-[10.5px] sm:text-[11.5px] mt-1 sm:mt-1.5' : 'h-[32px] sm:h-[34px] lg:h-[32px] xl:h-[34px] text-[11.5px] sm:text-[12.5px] mt-2 sm:mt-2.5') : 'h-[40px] sm:h-[44px] lg:h-[40px] xl:h-[42px] 2xl:h-[46px] text-[11.5px] sm:text-[12.5px] mt-2 sm:mt-2.5'} ${isActionsDisabled
              ? 'bg-gray-600/50 text-white/50 border border-gray-500/30 cursor-not-allowed'
              : isOrgs
                ? 'border-2 border-[#00D2D3] text-[#00D2D3] hover:bg-[#00D2D3]/20 shadow-[0_0_12px_rgba(0,210,211,0.2)]'
                : 'border-2 border-[#904DB0] bg-[#904DB0]/20 hover:bg-[#904DB0]/40 text-white shadow-[0_0_12px_rgba(144,77,176,0.2)]'
              } disabled:opacity-50 font-medium rounded-[14px] sm:rounded-[87.5px] transition-all flex items-center justify-center uppercase tracking-wider flex-shrink-0`}
          >
            {isLoading ? (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              subscribeLabel || (isOrgs ? (t?.contactUs || "Contact Us") : (t?.subscribe || "Subscribe"))
            )}
          </button>
        )
      )}

      {isActive && !isCancelPending && onCancel && !isOrgsPlanActive && (
        <button
          onClick={onCancel}
          disabled={isCancelling}
          className={`w-full ${isCompact ? 'h-[26px] sm:h-[28px] mt-1 text-[10px] sm:text-[10.5px]' : 'h-[32px] mt-1.5 text-[11px]'} border border-red-400 text-red-400 hover:bg-red-400/10 disabled:opacity-50 font-medium rounded-[87.5px] transition-colors flex items-center justify-center uppercase tracking-wider flex-shrink-0`}
        >
          {isCancelling ? (
            <span className="w-4 h-4 border-2 border-red-300/40 border-t-red-400 rounded-full animate-spin" />
          ) : (
            t?.cancelPlan || "Cancel Plan"
          )}
        </button>
      )}
    </div>
  );
};

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isShow, setIsShow] = useState(false);
  const [isSubmiting, setSubmiting] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [activeTab, setActiveTab] = useState(
    location.state?.tab ||
    (window.location.pathname === "/plans" ||
      window.location.search.includes("success") ||
      window.location.search.includes("fail")
      ? "plans"
      : "login")
  );
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  const [resetLinkSent, setResetLinkSent] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [plans, setPlans] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem("linko_plans_cache") || "[]");
      return Array.isArray(cached) ? cached : [];
    } catch (_) {
      return [];
    }
  });
  const [subscribingId, setSubscribingId] = useState(null);
  const [activeSub, setActiveSub] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem("linko_active_sub") || "null");
      return cached;
    } catch (_) {
      return null;
    }
  });
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const reduxUser = useSelector((state) => state.user?.user);
  const reduxTokenBalance = useSelector((state) => state.user?.tokenBalance);
  const reduxTokenLimit = useSelector((state) => state.user?.tokenLimit);

  const [tokenBalance, setTokenBalance] = useState(() => {
    if (typeof reduxTokenBalance === "number") return reduxTokenBalance;
    try {
      const cached = JSON.parse(localStorage.getItem("linko_token_status") || "null");
      return typeof cached?.availableTokens === "number" ? cached.availableTokens : null;
    } catch (_) { return null; }
  });
  const [tokenLimit, setTokenLimit] = useState(() => {
    if (typeof reduxTokenLimit === "number") return reduxTokenLimit;
    try {
      const cached = JSON.parse(localStorage.getItem("linko_token_status") || "null");
      return typeof cached?.tokenTotal === "number" ? cached.tokenTotal : null;
    } catch (_) { return null; }
  });
  const [isTokenLoading, setIsTokenLoading] = useState(() => {
    try {
      if (typeof reduxTokenBalance === "number" && typeof reduxTokenLimit === "number") {
        return false;
      }
      const cached = JSON.parse(localStorage.getItem("linko_token_status") || "null");
      const hasCached = cached && typeof cached?.availableTokens === "number" && typeof cached?.tokenTotal === "number";
      return !hasCached;
    } catch (_) {
      return false;
    }
  });

  useEffect(() => {
    if (reduxTokenBalance !== undefined && reduxTokenBalance !== null) {
      setTokenBalance(reduxTokenBalance);
      setIsTokenLoading(false);
    }
  }, [reduxTokenBalance]);

  useEffect(() => {
    if (reduxTokenLimit !== undefined && reduxTokenLimit !== null) {
      setTokenLimit(reduxTokenLimit);
    }
  }, [reduxTokenLimit]);
  const [purchasingTokens, setPurchasingTokens] = useState(false);
  const [freeTrialDays, setFreeTrialDays] = useState(() => {
    try {
      const cachedExpire = localStorage.getItem("linko_free_trial_expire_date");
      if (cachedExpire) {
        const days = getDaysRemaining(cachedExpire);
        return days > 0 ? days : 0;
      }
      const cached = Number(localStorage.getItem("linko_free_trial_days") || "0");
      return !isNaN(cached) && cached > 0 ? cached : 0;
    } catch (_) {
      return 0;
    }
  });
  const [confirmSwitch, setConfirmSwitch] = useState(null);
  const [showContactUs, setShowContactUs] = useState(false);
  const [isContactUsSubmitting, setIsContactUsSubmitting] = useState(false);
  const [contactUsSuccess, setContactUsSuccess] = useState(false);
  const [contactUsDisabled, setContactUsDisabled] = useState(() => {
    if (localStorage.getItem("linko_rejection_note")) return false;
    return localStorage.getItem("linko_contact_sent") === "true";
  });
  const [isCancelPending, setIsCancelPending] = useState(() => {
    try {
      if (localStorage.getItem("linko_cancel_at_period_end") === "true") {
        return true;
      }
      const active = JSON.parse(localStorage.getItem("linko_active_sub") || "null");
      return Boolean(active?.cancel_at_period_end);
    } catch (_) {
      return false;
    }
  });
  const [showReEnablePopup, setShowReEnablePopup] = useState(false);
  const [isReEnabling, setIsReEnabling] = useState(false);
  const [showAlreadySentPopup, setShowAlreadySentPopup] = useState(false);
  const [rejectionNote, setRejectionNote] = useState(() => (localStorage.getItem("linko_rejection_note") || ""));
  const [compactPlanCards, setCompactPlanCards] = useState(
    typeof window !== "undefined" && (window.innerHeight < 780 || window.innerWidth < 1200)
  );

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-height: 779px), (max-width: 1199px)");
    const update = () => setCompactPlanCards(mq.matches);
    update();
    if (mq.addEventListener) {
      mq.addEventListener("change", update);
    } else if (mq.addListener) {
      mq.addListener(update);
    }
    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener("change", update);
      } else if (mq.removeListener) {
        mq.removeListener(update);
      }
    };
  }, []);

  const sessionExpired =
    new URLSearchParams(window.location.search).get("reason") ===
    "session_expired";

  const { language, switchLanguage, t } = useLanguage();

  const isLoggedIn = !!localStorage.getItem("linko_web");
  const isMissingVal = (v) => !v || v === "undefined" || v === "null";

  const rawLocalPlan = readStoredPlanName();
  const localPlan = !isMissingVal(rawLocalPlan) ? rawLocalPlan : "";
  const storedPlanName = (
    localPlan ||
    reduxUser?.plan_name ||
    reduxUser?.trainingGroupId?.plan_name ||
    activeSub?.subscription_id?.plan_name ||
    activeSub?.plan_name ||
    ""
  ).toLowerCase();

  const rawLocalGroup = readStoredGroupName();
  const localGroup = !isMissingVal(rawLocalGroup) ? rawLocalGroup : "";
  const storedGroupName = (
    localGroup ||
    reduxUser?.trainingGroupId?.groupName ||
    ""
  ).toLowerCase();

  const isAdminProvide = storedPlanName.includes("adminprovide");
  const isOrgsActive =
    isAdminProvide ||
    (!isMissingVal(storedPlanName) && (storedPlanName.includes("org") || storedPlanName.includes("organiza"))) ||
    (!isMissingVal(storedGroupName) && Boolean(storedGroupName)) ||
    Boolean(reduxUser?.trainingGroupId);
  const isSubscribed =
    !isAdminProvide && (readStoredIsSubscription() || !!activeSub);
  const hasAnyPlan = isSubscribed || isAdminProvide || isOrgsActive;
  const hasAnyBothButtons = Boolean(isLoggedIn && !isOrgsActive && !isCancelPending && (isSubscribed || hasAnyPlan));

  const showBackToApp = isLoggedIn && hasAnyPlan && activeTab === "plans";
  const hideLeftPanel = false;

  const rightPlansRef = useRef(null);
  const leftWrapperRef = useRef(null);
  const [rightPlansTopOffset, setRightPlansTopOffset] = useState(0);

  useEffect(() => {
    if (activeTab !== "plans") {
      setRightPlansTopOffset(0);
      return;
    }

    const updateOffset = () => {
      if (rightPlansRef.current && typeof window !== "undefined" && window.innerWidth >= 1024) {
        const wrapper = leftWrapperRef.current || rightPlansRef.current.parentElement;
        if (wrapper) {
          const wrapperRect = wrapper.getBoundingClientRect();
          const cardRect = rightPlansRef.current.getBoundingClientRect();
          const offset = Math.max(0, Math.round(cardRect.top - wrapperRect.top));
          setRightPlansTopOffset(offset);
        }
      } else {
        setRightPlansTopOffset(0);
      }
    };

    updateOffset();
    const timer1 = setTimeout(updateOffset, 50);
    const timer2 = setTimeout(updateOffset, 200);
    window.addEventListener("resize", updateOffset);
    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined" && rightPlansRef.current) {
      resizeObserver = new ResizeObserver(updateOffset);
      resizeObserver.observe(rightPlansRef.current);
      if (rightPlansRef.current.parentElement) {
        resizeObserver.observe(rightPlansRef.current.parentElement);
      }
    }

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      window.removeEventListener("resize", updateOffset);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [activeTab, isLoggedIn, hasAnyPlan, isSubscribed, isOrgsActive, tokenBalance, tokenLimit, isTokenLoading]);

  useEffect(() => {
    if (sessionExpired) {
      localStorage.removeItem("linko_web");
      localStorage.removeItem("is_subscription");
      localStorage.removeItem("plan_name");
      localStorage.removeItem("userId");
      localStorage.removeItem("username");
      localStorage.removeItem("groupname");
      localStorage.removeItem("groupImage");
      localStorage.removeItem("linko_free_trial_days");
      localStorage.removeItem("linko_free_trial_expire_date");
      localStorage.removeItem("linko_cancel_at_period_end");
      localStorage.removeItem("linko_active_sub");
      toastError("Your session has expired. Please login again.");
      const newUrl =
        window.location.pathname +
        window.location.search.replace(/[?&]reason=session_expired/, "");
      window.history.replaceState({}, "", newUrl);
    }
  }, [sessionExpired]);

  useEffect(() => {
    let timer;
    if (resendCountdown > 0) {
      timer = setInterval(() => {
        setResendCountdown((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [resendCountdown]);

  const handleForgotPassword = async () => {
    if (!forgotEmail || isResetting || resendCountdown > 0) return;
    setIsResetting(true);
    try {
      await AuthService.forgotPassword(forgotEmail);
      setResetLinkSent(true);
      setResendCountdown(30);
    } catch (error) {
      toastError(localizeAuthError(error, t) || t.somethingWentWrong);
    } finally {
      setIsResetting(false);
    }
  };


  const applyUserData = useCallback((data) => {
    if (!data) return;
    persistProfileToStorage(data);

    const isFreeTrial =
      data.is_free_triee_use === true ||
      data.is_free_trial_use === true ||
      Boolean(data.free_trial_expire_date);

    const trialExpireDate =
      data.free_trial_expire_date ||
      data.trial_expire_date ||
      data?.current_subscription?.free_trial_expire_date ||
      data?.current_subscription?.trial_expire_date ||
      (isFreeTrial ? data.subscription_expiresAt : null);

    if (trialExpireDate) {
      const days = getDaysRemaining(trialExpireDate);
      if (days > 0) {
        setFreeTrialDays(days);
        try {
          localStorage.setItem("linko_free_trial_days", String(days));
          localStorage.setItem("linko_free_trial_expire_date", trialExpireDate);
        } catch (_) { }
      } else {
        setFreeTrialDays(0);
        try {
          localStorage.removeItem("linko_free_trial_days");
          localStorage.removeItem("linko_free_trial_expire_date");
        } catch (_) { }
      }
    } else if (!isFreeTrial) {
      setFreeTrialDays(0);
      try {
        localStorage.removeItem("linko_free_trial_days");
        localStorage.removeItem("linko_free_trial_expire_date");
      } catch (_) { }
    }

    if (data.is_subscription && data.current_plan_id) {
      setActiveSub((prev) => {
        if (prev?.subscription_id?._id === data.current_plan_id && prev.expire_date && prev.free_trial_expire_date) {
          return prev;
        }
        const updated = prev ? {
          ...prev,
          status: "active",
          plan_name: data.plan_name,
          free_trial_expire_date: trialExpireDate || prev.free_trial_expire_date,
          is_free_triee_use: data.is_free_triee_use ?? prev.is_free_triee_use,
          cancel_at_period_end: data.cancel_at_period_end !== undefined ? data.cancel_at_period_end : prev.cancel_at_period_end,
        } : {
          subscription_id: { _id: data.current_plan_id },
          status: "active",
          plan_name: data.plan_name,
          free_trial_expire_date: trialExpireDate,
          is_free_triee_use: data.is_free_triee_use,
          cancel_at_period_end: data.cancel_at_period_end,
        };
        try {
          localStorage.setItem("linko_active_sub", JSON.stringify(updated));
        } catch (_) { }
        return updated;
      });
      if (data.cancel_at_period_end === true) {
        setIsCancelPending(true);
        try {
          localStorage.setItem("linko_cancel_at_period_end", "true");
        } catch (_) { }
      } else if (data.cancel_at_period_end === false) {
        setIsCancelPending(false);
        try {
          localStorage.removeItem("linko_cancel_at_period_end");
        } catch (_) { }
      }
    }

    if (data.note) {
      setRejectionNote(data.note);
      setContactUsDisabled(false);
      try {
        localStorage.setItem("linko_rejection_note", data.note);
        localStorage.removeItem("linko_contact_sent");
      } catch (_) { }
    } else {
      setRejectionNote("");
      try {
        localStorage.removeItem("linko_rejection_note");
      } catch (_) { }
      const isSent =
        localStorage.getItem("linko_contact_sent") === "true" ||
        data.is_contactus === true ||
        data.is_contact_request === true ||
        data.contact_request === true ||
        data.is_request === true;
      setContactUsDisabled(Boolean(isSent));
      if (isSent) {
        try {
          localStorage.setItem("linko_contact_sent", "true");
        } catch (_) { }
      }
    }

    const avail =
      data.available_tokens !== undefined
        ? Number(data.available_tokens)
        : data.availableTokens !== undefined
          ? Number(data.availableTokens)
          : data.tokenBalance !== undefined
            ? Number(data.tokenBalance)
            : null;
    if (avail !== null && !isNaN(avail)) {
      setTokenBalance(avail);
    }
    const tot =
      data.total_tokens !== undefined
        ? Number(data.total_tokens)
        : data.totalTokens !== undefined
          ? Number(data.totalTokens)
          : data.tokenTotal !== undefined
            ? Number(data.tokenTotal)
            : data.tokenLimit !== undefined
              ? Number(data.tokenLimit)
              : null;
    if (tot !== null && !isNaN(tot)) {
      setTokenLimit(tot);
    }
  }, []);

  useEffect(() => {
    const syncStatus = () => {
      const note = localStorage.getItem("linko_rejection_note") || (localStorage.getItem("linko_contact_sent") === "true" ? "" : (reduxUser?.note || ""));
      setRejectionNote(note);
      if (note) {
        setContactUsDisabled(false);
      } else {
        const isSent =
          localStorage.getItem("linko_contact_sent") === "true" ||
          reduxUser?.is_contactus === true ||
          reduxUser?.is_contact_request === true ||
          reduxUser?.contact_request === true ||
          reduxUser?.is_request === true;
        setContactUsDisabled(Boolean(isSent));
      }
    };

    syncStatus();
    window.addEventListener("contactStatusChanged", syncStatus);
    window.addEventListener("planUpdated", syncStatus);
    window.addEventListener("profileUpdated", syncStatus);
    window.addEventListener("storage", syncStatus);
    return () => {
      window.removeEventListener("contactStatusChanged", syncStatus);
      window.removeEventListener("planUpdated", syncStatus);
      window.removeEventListener("profileUpdated", syncStatus);
      window.removeEventListener("storage", syncStatus);
    };
  }, [reduxUser]);

  useEffect(() => {
    if (reduxUser) {
      applyUserData(reduxUser);
    }
  }, [reduxUser, applyUserData]);

  const fetchUserData = async (force = false) => {
    const token = localStorage.getItem("linko_web");
    if (!token) return;

    if (!force && reduxUser) {
      applyUserData(reduxUser);
      return;
    }

    setIsTokenLoading(true);
    try {
      const response = await UserService.getProfile();
      if (response?.data?.data) {
        const data = response.data.data;
        dispatch(setUser(data));
        dispatch(setTokenStatus(data));
        persistProfileToStorage(data);
        applyUserData(data);
        if (data.is_subscription !== undefined) {
          localStorage.setItem("is_subscription", data.is_subscription ? "true" : "false");
        }
        if (data.plan_name) {
          localStorage.setItem("plan_name", data.plan_name);
        }
        window.dispatchEvent(new Event("profileUpdated"));
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        localStorage.removeItem("linko_web");
        localStorage.removeItem("is_subscription");
        localStorage.removeItem("plan_name");
        navigate("/login");
        toastError("Your session has expired. Please login again.");
      } else {
        console.error("Failed to fetch user data:", error);
      }
    } finally {
      setIsTokenLoading(false);
    }
  };

  const fetchPlans = useCallback(async () => {
    try {
      const res = await PaymentService.getPlans();
      if (res?.data?.data) {
        setPlans(res.data.data);
        try {
          localStorage.setItem("linko_plans_cache", JSON.stringify(res.data.data));
        } catch (_) { }
      }
    } catch (error) {
      console.error("Failed to fetch plans:", error);
    }
  }, []);

  const fetchActiveSub = useCallback(async () => {
    const token = localStorage.getItem("linko_web");
    if (!token) return;
    try {
      const res = await PaymentService.getActiveSubscription();
      const subs = res?.data?.data;
      if (subs?.length > 0) {
        setActiveSub(subs[0]);
        try {
          localStorage.setItem("linko_active_sub", JSON.stringify(subs[0]));
        } catch (_) { }
        if (subs[0].cancel_at_period_end) {
          setIsCancelPending(true);
          try {
            localStorage.setItem("linko_cancel_at_period_end", "true");
          } catch (_) { }
        } else {
          setIsCancelPending(false);
          try {
            localStorage.removeItem("linko_cancel_at_period_end");
          } catch (_) { }
        }
        const trialExpireDate = subs[0].free_trial_expire_date
          || subs[0].trial_expire_date
          || subs[0].subscription_id?.free_trial_expire_date
          || subs[0].subscription_id?.trial_expire_date
          || localStorage.getItem("linko_free_trial_expire_date");
        if (trialExpireDate) {
          const days = getDaysRemaining(trialExpireDate);
          if (days > 0) {
            setFreeTrialDays(days);
            try {
              localStorage.setItem("linko_free_trial_days", String(days));
            } catch (_) { }
          } else {
            setFreeTrialDays(0);
            try {
              localStorage.removeItem("linko_free_trial_days");
              localStorage.removeItem("linko_free_trial_expire_date");
            } catch (_) { }
          }
        }
      } else {
        setActiveSub(null);
        setIsCancelPending(false);
        try {
          localStorage.removeItem("linko_active_sub");
          localStorage.removeItem("linko_cancel_at_period_end");
        } catch (_) { }
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        localStorage.removeItem("linko_web");
        localStorage.removeItem("is_subscription");
        localStorage.removeItem("plan_name");
        navigate("/login");
        toastError("Your session has expired. Please login again.");
      } else {
        console.error("Failed to fetch subscription:", error);
      }
    }
  }, [navigate]);

  useEffect(() => {
    fetchPlans();
    if (localStorage.getItem("linko_web")) {
      fetchActiveSub();
      fetchUserData(true);
    }
  }, [fetchPlans, fetchActiveSub]);

  useEffect(() => {
    if (activeSub) {
      const trialExpireDate =
        activeSub.free_trial_expire_date ||
        activeSub.trial_expire_date ||
        activeSub.subscription_id?.free_trial_expire_date ||
        activeSub.subscription_id?.trial_expire_date ||
        localStorage.getItem("linko_free_trial_expire_date");
      if (trialExpireDate) {
        const days = getDaysRemaining(trialExpireDate);
        if (days > 0) {
          setFreeTrialDays((prev) => (prev === days ? prev : days));
          try {
            localStorage.setItem("linko_free_trial_days", String(days));
          } catch (_) { }
        } else {
          setFreeTrialDays(0);
          try {
            localStorage.removeItem("linko_free_trial_days");
            localStorage.removeItem("linko_free_trial_expire_date");
          } catch (_) { }
        }
      }
    }
  }, [activeSub]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (location.search.includes("success") || params.has("session_id")) {
      setPaymentStatus("success");
      const refreshData = async () => {
        await fetchUserData(true);
        try {
          const res = await PaymentService.getActiveSubscription();
          const subs = res?.data?.data;
          if (subs?.length > 0) {
            setActiveSub(subs[0]);
            try {
              localStorage.setItem("linko_active_sub", JSON.stringify(subs[0]));
            } catch (_) { }
            if (subs[0].cancel_at_period_end) {
              setIsCancelPending(true);
            } else {
              setIsCancelPending(false);
            }
          }
        } catch (error) {
          console.error("Failed to fetch subscription:", error);
        }
      };
      refreshData();
      const retryTimer = setTimeout(() => {
        refreshData();
      }, 2000);
      navigate(location.pathname, { replace: true });
      return () => clearTimeout(retryTimer);
    } else if (params.has("fail") || location.search.includes("fail")) {
      setPaymentStatus("fail");
      navigate(location.pathname, { replace: true });
    }
  }, [location.search]);

  useEffect(() => {
    if (isLoggedIn) {
      setActiveTab("plans");
      return;
    }
    if (location.state?.tab) {
      setActiveTab(location.state.tab);
    }
  }, [location.state, isLoggedIn]);

  const handleContactUsSubmit = async (form) => {
    try {
      setIsContactUsSubmitting(true);
      const payload = {
        email: form.email,
        name: form.name,
        phone_no: form.phone_no,
        country_code: form.country_code,
        note: form.note,
      };
      await PaymentService.contactUsPlan(payload);
      setShowContactUs(false);
      setContactUsSuccess(true);
      setContactUsDisabled(true);
      setRejectionNote("");
      try {
        localStorage.setItem("linko_contact_sent", "true");
        localStorage.removeItem("linko_rejection_note");
      } catch (_) { }
      dispatch(updateUserProfile({ is_contact_request: true, is_contactus: true, note: null }));
      window.dispatchEvent(new CustomEvent("contactStatusChanged", { detail: { sent: true } }));
      window.dispatchEvent(new Event("planUpdated"));
      window.dispatchEvent(new Event("profileUpdated"));
      await fetchUserData();
    } catch (error) {
      toastError(
        error?.response?.data?.message ||
        "Failed to send request. Please try again."
      );
    } finally {
      setIsContactUsSubmitting(false);
    }
  };

  const handleReEnablePayment = async () => {
    setIsReEnabling(true);
    try {
      const planId = activeSub?.subscription_id?._id;
      if (!planId) {
        toastError("No active plan found to re-enable.");
        setIsReEnabling(false);
        return;
      }
      const response = await PaymentService.purchasePlan(planId);
      const url = response?.data?.data?.checkoutUrl;
      if (url) {
        localStorage.setItem("stripe_pending", "true");
        window.location.href = url;
      } else {
        toastSuccess(
          response?.data?.message || "Payment re-enabled successfully. You will be billed on the next renewal date."
        );
        setShowReEnablePopup(false);
        setIsCancelPending(false);
        try {
          localStorage.removeItem("linko_cancel_at_period_end");
        } catch (_) { }
        setActiveSub((prev) => {
          if (!prev) return prev;
          const updated = { ...prev, cancel_at_period_end: false };
          try {
            localStorage.setItem("linko_active_sub", JSON.stringify(updated));
          } catch (_) { }
          return updated;
        });
        dispatch(updateUserProfile({ cancel_at_period_end: false }));
        await Promise.all([
          fetchUserData(true),
          fetchActiveSub(),
        ]);
      }
    } catch (error) {
      toastError(
        error?.response?.data?.message || "Failed to re-enable payment. Please try again."
      );
    } finally {
      setIsReEnabling(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!isLoggedIn) {
      toastError(t.loginFirstMsg || "Please log in first to subscribe.");
      setActiveTab("login");
      return;
    }
    if (isOrgsActive) {
      toastError(t.orgsPlanActiveMsg || "ORGs plan is active. Please contact support to switch plans.");
      return;
    }
    const plan = plans.find((p) => p._id === planId);
    if (plan?.interval === "free") {
      await doPurchase(planId);
      return;
    }
    if (
      activeSub &&
      activeSub.status === "active" &&
      !isCancelPending &&
      activeSub.subscription_id?._id !== planId
    ) {
      setConfirmSwitch({ newPlanId: planId });
      return;
    }
    if (isCancelPending) {
      await doPurchase(planId);
      return;
    }
    await doPurchase(planId);
  };

  const doPurchase = async (planId) => {
    try {
      setSubscribingId(planId);
      const res = await PaymentService.purchasePlan(planId);
      const url = res?.data?.data?.checkoutUrl;
      if (url) {
        localStorage.setItem("stripe_pending", "true");
        window.location.href = url;
      } else {
        toastSuccess(res?.data?.message || "Plan activated successfully");
        await Promise.all([
          fetchUserData(true),
          fetchActiveSub(),
        ]);
      }
    } catch (error) {
      toastError(
        error?.response?.data?.message || "Failed to start checkout"
      );
    } finally {
      setSubscribingId(null);
    }
  };

  const handleConfirmSwitch = async () => {
    const { newPlanId } = confirmSwitch;
    setConfirmSwitch(null);
    try {
      setSubscribingId(newPlanId);
      await PaymentService.cancelPlan(activeSub.subscription_id?._id);
      const res = await PaymentService.purchasePlan(newPlanId);
      const url = res?.data?.data?.checkoutUrl;
      if (url) {
        localStorage.setItem("stripe_pending", "true");
        window.location.href = url;
      }
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to switch plan");
    } finally {
      setSubscribingId(null);
    }
  };

  const handleCancel = async (planId) => {
    if (isOrgsActive) {
      toastError(t.orgsPlanActiveMsg || "ORGs plan is active. Please contact support to cancel.");
      return;
    }
    setIsCancelling(true);
    try {
      const response = await PaymentService.cancelPlan(planId);
      toastSuccess(
        response?.data?.message ||
        "Plan will be cancelled at the end of the billing period."
      );
      setIsCancelPending(true);
      try {
        localStorage.setItem("linko_cancel_at_period_end", "true");
      } catch (_) { }
      setShowCancelConfirm(false);

      setActiveSub((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, cancel_at_period_end: true };
        try {
          localStorage.setItem("linko_active_sub", JSON.stringify(updated));
        } catch (_) { }
        return updated;
      });

      dispatch(updateUserProfile({ cancel_at_period_end: true }));

      await Promise.all([
        fetchUserData(true),
        fetchActiveSub(),
      ]);
    } catch (error) {
      toastError(
        error?.response?.data?.message || "Failed to cancel subscription"
      );
    } finally {
      setIsCancelling(false);
    }
  };

  const handlePurchaseTokens = async () => {
    const tokenPlan = plans.find(
      (p) =>
        p.interval === "free" && p.plan_name.toLowerCase().includes("token")
    );
    if (!tokenPlan) {
      toastError("Token plan not available");
      return;
    }
    setPurchasingTokens(true);
    try {
      const res = await PaymentService.purchasePlan(tokenPlan._id);
      const url = res?.data?.data?.checkoutUrl;
      if (url) {
        localStorage.setItem("stripe_pending", "true");
        window.location.href = url;
      } else {
        toastSuccess(res?.data?.message || "Tokens purchased successfully");
        await Promise.all([
          fetchUserData(true),
          fetchActiveSub(),
        ]);
      }
    } catch (error) {
      toastError(
        error?.response?.data?.message || "Failed to purchase tokens"
      );
    } finally {
      setPurchasingTokens(false);
    }
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setSubmiting(true);
      const response = await AuthService.googleLogin(
        credentialResponse.credential
      );
      if (response) {
        setIsShow(true);
        const userData = response?.data?.data;
        const token = response?.data?.token;
        try { localStorage.removeItem("linko_transcribe_result"); } catch (_e) { }
        try { indexedDB.deleteDatabase("linko_transcribe_db"); } catch (_e) { }
        localStorage.setItem("linko_web", token);
        persistProfileToStorage(userData);
        dispatch(setUser(userData));
        initGlobalSocket(dispatch);
        window.dispatchEvent(new Event("authChanged"));
        await fetchUserData();
        setTimeout(() => {
          setIsShow(false);
          toastSuccess(t.loginSuccess);
          if (userData?.is_subscription) {
            navigate("/chat");
          } else {
            navigate("/plans", { state: { tab: "plans" } });
          }
        }, 1000);
      }
    } catch (error) {
      toastError(localizeAuthError(error, t) || t.googleLoginError);
    } finally {
      setSubmiting(false);
    }
  };

  const handleSubmit = async (values) => {
    const payload = {
      email: values?.email.toLowerCase(),
      password: values?.password,
    };
    try {
      setSubmiting(true);
      const response = await AuthService?.login(payload);
      if (response) {
        setIsShow(true);
        const userData = response?.data?.data;
        const token = response?.data?.token;
        try { localStorage.removeItem("linko_transcribe_result"); } catch (_e) { }
        try { indexedDB.deleteDatabase("linko_transcribe_db"); } catch (_e) { }
        localStorage.setItem("linko_web", token);
        persistProfileToStorage(userData);
        dispatch(setUser(userData));
        initGlobalSocket(dispatch);
        window.dispatchEvent(new Event("authChanged"));
        await fetchUserData();
        setTimeout(() => {
          setIsShow(false);
          toastSuccess(t.loginSuccess);
          if (userData?.is_subscription) {
            navigate("/chat");
          } else {
            navigate("/plans", { state: { tab: "plans" } });
          }
        }, 1000);
      }
    } catch (error) {
      toastError(localizeAuthError(error, t) || t.somethingWentWrong);
    } finally {
      setSubmiting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().required(t.emailReq),
      password: Yup.string().required(t.passwordReq),
    }),
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  return (
    <>
      {paymentStatus === "success" && (
        <PaymentSuccessPopup
          t={t}
          onClose={async () => {
            localStorage.setItem("is_subscription", "true");
            setPaymentStatus(null);
            await fetchUserData();
            navigate("/chat");
          }}
        />
      )}
      {paymentStatus === "fail" && (
        <PaymentFailPopup t={t} onClose={() => setPaymentStatus(null)} />
      )}
      {showReEnablePopup && (
        <ReEnablePaymentPopup
          t={t}
          onClose={() => setShowReEnablePopup(false)}
          onConfirm={handleReEnablePayment}
          isProcessing={isReEnabling}
        />
      )}
      {showAlreadySentPopup && (
        <AlreadySentPopup
          t={t}
          onClose={() => setShowAlreadySentPopup(false)}
        />
      )}
      {showContactUs && (
        <ContactUsPopup
          t={t}
          onClose={() => setShowContactUs(false)}
          onSubmit={handleContactUsSubmit}
          isSubmitting={isContactUsSubmitting}
          isSuccess={false}
          prefilledEmail={readStoredEmail() || ""}
        />
      )}
      {contactUsSuccess && (
        <ContactUsPopup
          t={t}
          onClose={() => setContactUsSuccess(false)}
          onSubmit={() => { }}
          isSubmitting={false}
          isSuccess={true}
        />
      )}
      {confirmSwitch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[linear-gradient(157.06deg,#1A0E3E_46.27%,#904DB0_94.25%)] rounded-[27px] border border-[rgba(199,191,229,0.20)] px-8 py-10 flex flex-col items-center gap-6 w-full max-w-[420px] mx-4 shadow-2xl">
            <div className="w-16 h-16 rounded-full bg-[#904DB0]/30 flex items-center justify-center">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path
                  d="M16 4v12M16 20v4"
                  stroke="#fff"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
                <circle cx="16" cy="28" r="1.5" fill="#fff" />
              </svg>
            </div>
            <div className="text-center">
              <h2 className="text-white font-extrabold text-[22px] mb-2">
                {t.switchPlanTitle || "Switch Plan?"}
              </h2>
              <p className="text-[#D2C5E8] text-[14px] font-light leading-relaxed">
                {t.switchPlanDesc || "Your current plan will be cancelled at the end of the billing period, then the new plan checkout will open."}
              </p>
            </div>
            <div className="flex gap-3 w-full">
              <button
                onClick={() => setConfirmSwitch(null)}
                className="flex-1 h-[46px] border border-[#904DB0] text-white font-semibold rounded-[87.5px] transition-colors text-[14px] hover:bg-white/10"
              >
                {t.reEnablePaymentCancel || "Cancel"}
              </button>
              <button
                onClick={handleConfirmSwitch}
                className="flex-1 h-[46px] bg-[#904DB0] hover:bg-[#7d3fa0] text-white font-semibold rounded-[87.5px] transition-colors text-[14px]"
              >
                {t.confirmSwitch || "Confirm Switch"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[linear-gradient(157.06deg,#1A0E3E_46.27%,#904DB0_94.25%)] rounded-[27px] border border-[rgba(199,191,229,0.20)] px-8 py-10 flex flex-col items-center gap-6 w-full max-w-[420px] mx-4 shadow-2xl">
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path
                  d="M16 4v12M16 20v4"
                  stroke="#fff"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
                <circle cx="16" cy="28" r="1.5" fill="#fff" />
              </svg>
            </div>
            <div className="text-center">
              <h2 className="text-white font-extrabold text-[22px] mb-2">
                {t.cancelPlanTitle || "Cancel Plan?"}
              </h2>
              <p className="text-[#D2C5E8] text-[14px] font-light leading-relaxed">
                {t.cancelPlanDesc || "Are you sure you want to cancel your subscription? You will lose access at the end of your billing period."}
              </p>
            </div>
            <div className="flex gap-3 w-full">
              <button
                onClick={() => setShowCancelConfirm(false)}
                disabled={isCancelling}
                className="flex-1 h-[46px] border border-[#904DB0] text-white font-semibold rounded-[87.5px] transition-colors text-[14px] hover:bg-white/10 disabled:opacity-50"
              >
                {t.keepPlan || "Keep Plan"}
              </button>
              <button
                onClick={() => handleCancel(showCancelConfirm)}
                disabled={isCancelling}
                className="flex-1 h-[46px] bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-semibold rounded-[87.5px] transition-colors text-[14px] flex items-center justify-center"
              >
                {isCancelling ? (
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  t.yesCancel || "Yes, Cancel"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {isShow ? (
        <LoginAnimation />
      ) : (
        <div className="flex flex-col lg:flex-row min-h-screen lg:h-screen w-full font-roboto relative lg:overflow-hidden">
          {/* Language toggle - centered at the bottom of the screen (desktop only) */}
          <div className={`hidden lg:block absolute bottom-2.5 lg:bottom-12 xl:bottom-12 2xl:bottom-16 left-1/2 -translate-x-1/2 z-40 pointer-events-auto auth-bottom-toggle ${activeTab === "plans" && isLoggedIn && hasAnyPlan ? "auth-bottom-toggle-tokens" : ""}`}>
            <div className="flex rounded-[22.5px] shadow-[inset_0_0_0_3px_#904DB0] h-[32px] lg:h-[34px] xl:h-[36px] 2xl:h-[42px] bg-[#F8F7FC] min-w-[130px] lg:min-w-[135px] xl:min-w-[145px] 2xl:min-w-[160px] overflow-hidden p-0 auth-bottom-toggle-box">
              <button
                type="button"
                onClick={() => switchLanguage("es")}
                className={`flex-1 h-full text-[12px] lg:text-[12.5px] xl:text-[13px] 2xl:text-[14px] uppercase flex items-center justify-center px-3 lg:px-4 xl:px-4 2xl:px-5 whitespace-nowrap transition-colors font-roboto leading-none tracking-normal cursor-pointer select-none rounded-r-full auth-bottom-toggle-btn ${language === "es"
                  ? "bg-[#904DB0] text-[#F8F7FC] font-black"
                  : "bg-transparent text-[#904DB0] font-semibold"
                  }`}
              >
                <span className="pointer-events-none">ESP</span>
              </button>
              <button
                type="button"
                onClick={() => switchLanguage("en")}
                className={`flex-1 h-full text-[12px] lg:text-[12.5px] xl:text-[13px] 2xl:text-[14px] uppercase flex items-center justify-center px-3 lg:px-4 xl:px-4 2xl:px-5 whitespace-nowrap transition-colors font-roboto leading-none tracking-normal cursor-pointer select-none rounded-l-full auth-bottom-toggle-btn ${language === "en"
                  ? "bg-[#904DB0] text-[#F8F7FC] font-black"
                  : "bg-transparent text-[#904DB0] font-semibold"
                  }`}
              >
                <span className="pointer-events-none">ENG</span>
              </button>
            </div>
          </div>

          {/* Mobile/tablet top bar */}
          <div className="lg:hidden w-full z-20 flex flex-col items-center pt-3 pb-3 gap-3 bg-[#1A0E3E]">
            {showBackToApp ? (
              <div className="w-full flex justify-center px-4">
                <button
                  onClick={() => navigate("/chat")}
                  className="flex items-center justify-center gap-2 w-full max-w-[420px] h-[40px] px-5 rounded-[22.5px] bg-[#904DB0] text-[#F8F7FC] text-[13px] font-black uppercase whitespace-nowrap shadow-[inset_0_0_0_3px_#904DB0] transition-colors hover:bg-[#7d3fa0]"
                >
                  ← {t.backToApp || "Back to App"}
                </button>
              </div>
            ) : null}

            {/* Mobile tabs for switching between Login and Plans */}
            {!isLoggedIn ? (
              <div className="flex rounded-full shadow-[inset_0_0_0_3px_#904DB0] h-[36px] bg-[#F8F7FC] min-w-[200px] overflow-hidden p-0">
                <button
                  type="button"
                  onClick={() => setActiveTab("plans")}
                  className={`w-1/2 h-full text-[12px] uppercase flex items-center justify-center px-4 whitespace-nowrap transition-colors font-roboto leading-none tracking-normal cursor-pointer select-none ${activeTab === "plans"
                    ? "bg-[#904DB0] text-[#F8F7FC] font-black rounded-r-full"
                    : "bg-transparent text-[#904DB0] font-semibold"
                    }`}
                >
                  <span className="pointer-events-none">{t.plans}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("login")}
                  className={`w-1/2 h-full text-[12px] uppercase flex items-center justify-center px-4 whitespace-nowrap transition-colors font-roboto leading-none tracking-normal cursor-pointer select-none ${activeTab === "login"
                    ? "bg-[#904DB0] text-[#F8F7FC] font-black rounded-l-full"
                    : "bg-transparent text-[#904DB0] font-semibold"
                    }`}
                >
                  <span className="pointer-events-none">{t.loginTab}</span>
                </button>
              </div>
            ) : null}
          </div>

          {/* Desktop tabs - aligned in height and position with Linko logo */}
          {!isLoggedIn ? (
            <div className="hidden lg:flex absolute top-3 lg:top-[26px] xl:top-[26px] 2xl:top-[39px] left-1/2 -translate-x-1/2 z-40 pointer-events-auto auth-top-tabs">
              <div className="flex rounded-full shadow-[inset_0_0_0_3px_#904DB0] h-[32px] lg:h-[36px] xl:h-[38px] 2xl:h-[52px] bg-[#F8F7FC] min-w-[170px] lg:min-w-[190px] xl:min-w-[210px] 2xl:min-w-[280px] overflow-hidden p-0 auth-top-tabs-box">
                <button
                  type="button"
                  onClick={() => setActiveTab("plans")}
                  className={`w-1/2 h-full text-[11px] lg:text-[12px] xl:text-[13px] 2xl:text-[16px] uppercase flex items-center justify-center px-3 lg:px-4 xl:px-5 2xl:px-6 whitespace-nowrap transition-colors font-roboto leading-none tracking-normal cursor-pointer select-none auth-top-tabs-btn ${activeTab === "plans"
                    ? "bg-[#904DB0] text-[#F8F7FC] font-black rounded-r-full"
                    : "bg-transparent text-[#904DB0] font-semibold"
                    }`}
                >
                  <span className="pointer-events-none">{t.plans}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("login")}
                  className={`w-1/2 h-full text-[11px] lg:text-[12px] xl:text-[13px] 2xl:text-[16px] uppercase flex items-center justify-center px-3 lg:px-4 xl:px-5 2xl:px-6 whitespace-nowrap transition-colors font-roboto leading-none tracking-normal cursor-pointer select-none auth-top-tabs-btn ${activeTab === "login"
                    ? "bg-[#904DB0] text-[#F8F7FC] font-black rounded-l-full"
                    : "bg-transparent text-[#904DB0] font-semibold"
                    }`}
                >
                  <span className="pointer-events-none">{t.loginTab}</span>
                </button>
              </div>
            </div>
          ) : null}

          {/* RIGHT SIDE */}
          <div className="w-full lg:w-1/2 lg:order-2 flex flex-col justify-between py-4 sm:py-6 lg:pt-6 lg:pb-3 xl:pt-6 xl:pb-3.5 2xl:py-8 px-4 sm:px-8 lg:px-6 xl:px-8 2xl:px-12 relative bg-[linear-gradient(157.06deg,#1A0E3E_46.27%,#904DB0_94.25%)] lg:h-full lg:overflow-y-auto overflow-x-hidden auth-outer-col-right">
            {/* Top spacing anchor to balance desktop top header */}
            <div className="hidden lg:block h-[36px] lg:h-[40px] xl:h-[44px] 2xl:h-[66px] shrink-0 auth-header" />

            <div className={`w-full flex-1 flex flex-col items-center justify-center py-1 sm:py-2 ${activeTab === "plans" ? "lg:pb-2 xl:pb-2 2xl:pb-4" : "lg:pb-14 xl:pb-14 2xl:pb-4"} auth-content-wrapper`}>
              {activeTab === "login" ? (
                showForgotPassword ? (
                  <div className="w-full max-w-[435px] sm:max-w-[455px] xl:max-w-[465px] 2xl:max-w-[500px] relative z-10 bg-[rgba(199,191,229,0.10)] rounded-[22px] sm:rounded-[27px] border border-[rgba(199,191,229,0.10)] px-6 sm:px-8 py-6 sm:py-7 lg:px-8 lg:py-7 xl:px-8 xl:py-7 2xl:px-10 2xl:py-9 flex flex-col auth-right-card">
                    {resetLinkSent ? (
                      <>
                        <div className="flex flex-col items-center justify-center gap-3 sm:gap-4 text-center my-auto">
                          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#904DB0]/30 flex items-center justify-center">
                            <svg
                              width="32"
                              height="32"
                              viewBox="0 0 36 36"
                              fill="none"
                              className="w-7 h-7 sm:w-8 sm:h-8"
                            >
                              <circle
                                cx="18"
                                cy="18"
                                r="18"
                                fill="#904DB0"
                                fillOpacity="0.3"
                              />
                              <path
                                d="M10 18l6 6 10-12"
                                stroke="#fff"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <h2 className="text-[20px] sm:text-[24px] lg:text-[22px] xl:text-[24px] 2xl:text-[28px] font-extrabold text-white">
                            {t.resetLinkSent}
                          </h2>
                          <p className="text-[#D2C5E8] text-[13px] sm:text-[13.5px] max-w-[320px]">
                            {t.resetLinkSentDesc}{" "}
                            <span className="text-white font-semibold break-all">
                              {forgotEmail}
                            </span>
                          </p>
                          <p className="text-[#D2C5E8] text-[12px] sm:text-[12.5px]">
                            {t.resetLinkSentNote}{" "}
                            <button
                              type="button"
                              disabled={resendCountdown > 0 || isResetting}
                              onClick={handleForgotPassword}
                              className={`text-[#C9A0E2] bg-transparent border-none ${resendCountdown > 0 || isResetting ? "opacity-50 cursor-not-allowed no-underline" : "underline cursor-pointer hover:text-white"}`}
                            >
                              {resendCountdown > 0
                                ? `${t.resendLink} (${resendCountdown}s)`
                                : t.resendLink}
                            </button>
                          </p>
                        </div>
                        <div className="w-full text-center text-[12px] xl:text-[14px] mt-4">
                          <button
                            type="button"
                            onClick={() => {
                              setShowForgotPassword(false);
                              setForgotEmail("");
                              setResetLinkSent(false);
                              setResendCountdown(0);
                            }}
                            className="bg-transparent border-none cursor-pointer text-white hover:text-[#D2C5E8] font-inherit hover:underline underline-offset-[4px] decoration-[#D2C5E8]/60"
                          >
                            {t.backToLogin || "← Back to log in"}
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <h2 className="text-[22px] sm:text-[24px] lg:text-[23px] xl:text-[25px] 2xl:text-[30px] font-extrabold text-white mb-1.5 text-left w-full">
                            {t.forgotPwd}
                          </h2>
                          <p className="text-[#D2C5E8] text-[12.5px] sm:text-[13.5px] leading-relaxed text-left w-full">
                            {t.forgotPwdDesc}
                          </p>
                        </div>

                        {/* Email input */}
                        <div className="flex flex-col gap-1.5 mt-3 sm:mt-4 2xl:mt-5 w-full">
                          <div className="relative">
                            <FaEnvelope className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-[15px] pointer-events-none" />
                            <input
                              type="email"
                              placeholder={t.emailPh}
                              value={forgotEmail}
                              onChange={(e) => setForgotEmail(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && forgotEmail && !isResetting) {
                                  handleForgotPassword();
                                }
                              }}
                              className="w-full h-[38px] lg:h-[36px] xl:h-[38px] 2xl:h-[44px] bg-white rounded-[8px] focus:outline-none text-gray-800 font-medium placeholder-gray-500 pl-10 pr-4 text-[13px] lg:text-[13px] xl:text-[13.5px] 2xl:text-[14.5px] auth-right-input"
                              autoComplete="off"
                            />
                          </div>
                        </div>

                        {/* Action buttons at bottom */}
                        <div className="flex flex-col gap-2.5 mt-4 sm:mt-5 2xl:mt-6 w-full">
                          <button
                            type="button"
                            onClick={handleForgotPassword}
                            disabled={isResetting || !forgotEmail}
                            className="w-full h-[38px] lg:h-[38px] xl:h-[40px] 2xl:h-[46px] bg-[#904DB0] hover:bg-[#7d3fa0] text-white font-semibold rounded-[87.5px] shadow-login-btn transition-colors flex justify-center items-center gap-[10.94px] text-[13.5px] lg:text-[14px] xl:text-[14.5px] 2xl:text-[15.5px] disabled:opacity-60 auth-right-btn"
                          >
                            {isResetting ? (
                              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                              t.sendResetLink
                            )}
                          </button>
                          <div className="text-center text-[12px] xl:text-[14px] mt-1">
                            <button
                              type="button"
                              onClick={() => {
                                setShowForgotPassword(false);
                                setForgotEmail("");
                              }}
                              className="bg-transparent border-none cursor-pointer text-white hover:text-[#D2C5E8] font-inherit hover:underline underline-offset-[4px] decoration-[#D2C5E8]/60"
                            >
                              {t.backToLogin || "← Back to log in"}
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="w-full max-w-[435px] sm:max-w-[455px] xl:max-w-[465px] 2xl:max-w-[500px] relative z-10 bg-[rgba(199,191,229,0.10)] rounded-[22px] sm:rounded-[27px] border border-[rgba(199,191,229,0.10)] px-6 sm:px-8 py-6 sm:py-7 lg:px-8 lg:py-7 xl:px-8 xl:py-7 2xl:px-10 2xl:py-9 flex flex-col auth-right-card">
                    <h2 className="text-[22px] sm:text-[24px] lg:text-[23px] xl:text-[25px] 2xl:text-[30px] font-extrabold text-white text-left w-full">
                      {t.loginTitle}
                    </h2>
                    {sessionExpired && (
                      <div className="mt-1.5 px-3 py-1 rounded-[8px] bg-red-500/20 border border-red-400/40 text-red-300 text-[11.5px] leading-snug">
                        Your session has expired. Please login again.
                      </div>
                    )}
                    <form
                      onSubmit={formik.handleSubmit}
                      className="flex flex-col mt-3 sm:mt-4 2xl:mt-4.5"
                      autoComplete="off"
                    >
                      <div className="flex flex-col">
                        <div>
                          <div className="relative">
                            <FaEnvelope className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-[15px] pointer-events-none" />
                            <input
                              type="email"
                              name="email"
                              placeholder={t.emailPh}
                              className={`w-full h-[38px] lg:h-[36px] xl:h-[38px] 2xl:h-[44px] bg-white rounded-[8px] focus:outline-none text-gray-800 font-medium placeholder-gray-500 pl-10 pr-4 text-[13px] lg:text-[13px] xl:text-[13.5px] 2xl:text-[14.5px] auth-right-input ${formik.touched.email && formik.errors.email ? "border-2 border-red-500" : ""
                                }`}
                              value={formik.values.email}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              autoComplete="off"
                            />
                          </div>
                          <div className="min-h-[18px] sm:min-h-[20px] mt-0.5">
                            {formik.touched.email && formik.errors.email && (
                              <div className="text-red-400 text-[11px] leading-tight">
                                {formik.errors.email}
                              </div>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="relative">
                            <FaLock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-[15px] pointer-events-none" />
                            <input
                              type={passwordVisible ? "text" : "password"}
                              name="password"
                              placeholder={t.passwordPh}
                              className={`w-full h-[38px] lg:h-[36px] xl:h-[38px] 2xl:h-[44px] bg-white rounded-[8px] focus:outline-none text-gray-800 font-medium placeholder-gray-500 pl-10 pr-10 text-[13px] lg:text-[13px] xl:text-[13.5px] 2xl:text-[14.5px] auth-right-input ${formik.touched.password && formik.errors.password ? "border-2 border-red-500" : ""
                                }`}
                              value={formik.values.password}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              autoComplete="new-password"
                            />
                            <button
                              type="button"
                              onClick={togglePasswordVisibility}
                              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-base"
                            >
                              {passwordVisible ? <FaEye /> : <FaEyeSlash />}
                            </button>
                          </div>
                          <div className="min-h-[18px] sm:min-h-[20px] mt-0.5">
                            {formik.touched.password && formik.errors.password && (
                              <div className="text-red-400 text-[11px] leading-tight">
                                {formik.errors.password}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end -mt-2 sm:-mt-2 mb-1">
                        <button
                          type="button"
                          onClick={() => setShowForgotPassword(true)}
                          className="text-white hover:text-[#D2C5E8] text-[11.5px] lg:text-[12px] xl:text-[12.5px] 2xl:text-[13.5px] underline decoration-[#D2C5E8]/60 underline-offset-[3px] bg-transparent border-none cursor-pointer"
                        >
                          {t.forgotPwd}
                        </button>
                      </div>
                      <button
                        type="submit"
                        disabled={isSubmiting}
                        className="w-full h-[38px] lg:h-[40px] xl:h-[44px] 2xl:h-[48px] bg-[#904DB0] hover:bg-[#7d3fa0] text-white font-semibold rounded-[87.5px] shadow-login-btn transition-colors flex justify-center items-center gap-[10.94px] text-[13.5px] lg:text-[14px] xl:text-[14.5px] 2xl:text-[15.5px] mt-3 sm:mt-3.5 lg:mt-4 xl:mt-4.5 2xl:mt-5 auth-right-btn"
                      >
                        {isSubmiting ? (
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          t.loginBtn
                        )}
                      </button>
                      <div className="flex items-center gap-2.5 my-2.5 sm:my-3 2xl:my-3.5">
                        <div className="flex-1 h-px bg-white/20"></div>
                        <span className="text-white/80 text-[11px] font-medium tracking-wider">
                          {t.or}
                        </span>
                        <div className="flex-1 h-px bg-white/20"></div>
                      </div>
                      <div className="flex justify-center relative h-[38px] lg:h-[38px] xl:h-[40px] 2xl:h-[44px] auth-google-btn-wrapper">
                        <button
                          type="button"
                          className="absolute inset-0 z-10 flex items-center justify-center gap-2 bg-white rounded-[8px] border border-gray-300 hover:bg-gray-50 transition-colors pointer-events-none"
                        >
                          <svg className="w-4 h-4 lg:w-4.5 lg:h-4.5 xl:w-5 xl:h-5 2xl:w-5 2xl:h-5 flex-shrink-0" viewBox="0 0 24 24">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                          </svg>
                          <span className="text-gray-700 font-medium text-[12.5px] lg:text-[13px] xl:text-[13.5px] 2xl:text-[14.5px]">{t.googleLogin}</span>
                        </button>
                        <div className="absolute inset-0 opacity-0">
                          <GoogleLogin
                            onSuccess={handleGoogleSuccess}
                            onError={() => toastError(t.googleLoginError)}
                            useOneTap={false}
                            type="standard"
                            size="large"
                            width="468"
                            text="signin_with"
                            shape="rectangular"
                          />
                        </div>
                      </div>
                      <div className="text-center text-[12px] xl:text-[13.5px] mt-3 sm:mt-3.5 2xl:mt-4">
                        <button
                          type="button"
                          onClick={() => navigate("/sign-up")}
                          className="bg-transparent border-none cursor-pointer text-white hover:text-[#D2C5E8] font-inherit hover:underline underline-offset-[4px] decoration-[#D2C5E8]/60"
                        >
                          {t.newHere}
                        </button>
                      </div>
                    </form>
                  </div>
                )
              ) : (
                /* PLANS Cards - ENLARGED */
                <div ref={rightPlansRef} className="flex flex-col items-center w-full max-w-full">
                  <div className={`grid grid-cols-1 sm:grid-cols-2 items-stretch justify-items-center gap-3.5 sm:gap-4 lg:gap-3.5 xl:gap-4 2xl:gap-8 z-10 py-0 px-2 sm:px-4 lg:px-3 xl:px-4 2xl:px-8 w-full max-w-[880px] ${(isLoggedIn && hasAnyPlan) || compactPlanCards ? 'lg:h-auto lg:min-h-[300px] xl:h-[360px] 2xl:h-[410px]' : 'lg:h-[385px] xl:h-[405px] 2xl:h-[465px]'} auth-plans-grid ${isLoggedIn && hasAnyPlan ? 'auth-plans-grid-tokens' : ''} ${hasAnyBothButtons ? 'has-two-buttons' : ''}`}>
                    {plans
                      .filter((p) => p.is_active !== false
                        && !(p.plan_name || "").toLowerCase().includes("orgs")
                        && !(p.plan_name || "").toLowerCase().includes("token"))
                      .length > 0 ? (
                      plans
                        .filter((p) => p.is_active !== false
                          && !(p.plan_name || "").toLowerCase().includes("orgs")
                          && !(p.plan_name || "").toLowerCase().includes("token"))
                        .map((plan) => {
                          const currentPlanName = readStoredPlanName().toLowerCase();
                          const isActive =
                            !isOrgsActive &&
                            ((activeSub?.subscription_id?._id === plan._id &&
                              activeSub?.status === "active") ||
                              currentPlanName === plan.plan_name.toLowerCase() ||
                              (isSubscribed &&
                                plan.plan_name.toLowerCase() === currentPlanName));

                          const trialMessage =
                            isActive && !isOrgsActive && !isCancelPending && freeTrialDays > 0
                              ? (t.trialDaysLeft ? t.trialDaysLeft.replace("{n}", freeTrialDays) : `You have ${freeTrialDays} day${freeTrialDays > 1 ? "s" : ""} left in your free trial`)
                              : null;

                          return (
                            <div key={plan._id} className="w-full flex-1 flex h-full">
                              <PlanCard
                                key={plan._id}
                                title={t.professionals}
                                price={plan.interval === "free" ? `$${plan.price}` : `$${plan.price}/${t.monthAbbr || "mo"}`}
                                bullets={t.professionalBullets}
                                subscribeLabel={isActive ? "Active Plan" : t.subscribe}
                                onSubscribe={() => handleSubscribe(plan._id)}
                                isLoading={subscribingId === plan._id}
                                isActive={isActive}
                                onCancel={
                                  isActive && !isCancelPending && !isOrgsActive
                                    ? () =>
                                      setShowCancelConfirm(
                                        activeSub?.subscription_id?._id ||
                                        plan._id
                                      )
                                    : undefined
                                }
                                isCancelling={isCancelling}
                                trialMessage={trialMessage}
                                isCancelPending={isCancelPending && isActive}
                                showReEnable={isActive && isCancelPending && !isOrgsActive}
                                onReEnable={() => setShowReEnablePopup(true)}
                                isReEnabling={isReEnabling}
                                t={t}
                                isOrgsPlanActive={isOrgsActive}
                                isLoggedIn={isLoggedIn}
                                isCompact={compactPlanCards || (isLoggedIn && hasAnyPlan)}
                              />
                            </div>
                          );
                        })
                    ) : (
                      <div className="w-full flex-1 flex h-full">
                        <PlanCard
                          title={t.professionals || "Professionals"}
                          price={t.profPrice || "$9"}
                          bullets={t.professionalBullets}
                          subscribeLabel={isSubscribed && !isOrgsActive ? (t.activePlan || "Active Plan") : (t.subscribe || "Subscribe")}
                          onSubscribe={() => {
                            if (!isLoggedIn) {
                              toastError(t.loginFirstMsg || "Please log in first to subscribe.");
                              setActiveTab("login");
                              return;
                            }
                            toastError("Please contact support for subscription options.");
                          }}
                          isLoading={false}
                          isActive={isSubscribed && !isOrgsActive}
                          t={t}
                          isOrgsPlanActive={isOrgsActive}
                          isLoggedIn={isLoggedIn}
                          isCompact={compactPlanCards || (isLoggedIn && hasAnyPlan)}
                          trialMessage={
                            isSubscribed && !isOrgsActive && freeTrialDays > 0
                              ? (t.trialDaysLeft ? t.trialDaysLeft.replace("{n}", freeTrialDays) : `You have ${freeTrialDays} day${freeTrialDays > 1 ? "s" : ""} left in your free trial`)
                              : null
                          }
                          isCancelPending={isCancelPending && isSubscribed && !isOrgsActive}
                        />
                      </div>
                    )}
                    <div className="w-full flex-1 flex h-full">
                      <PlanCard
                        title={t.orgs || "Organizations"}
                        price={null}
                        bullets={t.orgsBullets}
                        subscribeLabel={
                          contactUsDisabled && !rejectionNote
                            ? (t.requestSent || "Request Sent")
                            : rejectionNote
                              ? (t.contactUs || "Contact Us")
                              : t.contactUs || "Contact Us"
                        }
                        isActive={isOrgsActive}
                        hideSubscribe={isOrgsActive && !rejectionNote}
                        rejectionNote={rejectionNote}
                        onSubscribe={() => {
                          if (contactUsDisabled && !rejectionNote) {
                            setShowAlreadySentPopup(true);
                            return;
                          }
                          if (isLoggedIn) {
                            setShowContactUs(true);
                          } else {
                            toastError(
                              t.loginFirstMsg ||
                              "Please log in first to subscribe."
                            );
                            setActiveTab("login");
                          }
                        }}
                        t={t}
                        isOrgsPlanActive={false}
                        isLoggedIn={isLoggedIn}
                        isCompact={compactPlanCards || (isLoggedIn && hasAnyPlan)}
                      />
                    </div>
                  </div>
                  {isLoggedIn && hasAnyPlan && (
                    <div className="w-full max-w-[880px] px-2 sm:px-4 lg:px-3 xl:px-4 2xl:px-8 mt-2 sm:mt-2.5">
                      {(() => {
                        const tokenPlan = plans.find(
                          (p) =>
                            p.interval === "free" &&
                            (p.plan_name || "").toLowerCase().includes("token")
                        );
                        const tokenPlanPrice = tokenPlan?.price ? `$${tokenPlan.price}` : "$4.99";
                        const tokenPurchaseCount = (() => {
                          if (reduxUser?.tokenpucesh !== undefined && reduxUser?.tokenpucesh !== null) return reduxUser.tokenpucesh;
                          if (reduxUser?.token_pucesh !== undefined && reduxUser?.token_pucesh !== null) return reduxUser.token_pucesh;
                          if (reduxUser?.tokenPurchase !== undefined && reduxUser?.tokenPurchase !== null) return reduxUser.tokenPurchase;
                          if (reduxUser?.token_purchase !== undefined && reduxUser?.token_purchase !== null) return reduxUser.token_purchase;
                          if (reduxUser?.tokenPurchased !== undefined && reduxUser?.tokenPurchased !== null) return reduxUser.tokenPurchased;
                          try {
                            const cached = JSON.parse(localStorage.getItem("linko_user_profile") || "null");
                            if (cached?.tokenpucesh !== undefined && cached?.tokenpucesh !== null) return cached.tokenpucesh;
                            if (cached?.token_pucesh !== undefined && cached?.token_pucesh !== null) return cached.token_pucesh;
                            if (cached?.tokenPurchase !== undefined && cached?.tokenPurchase !== null) return cached.tokenPurchase;
                            if (cached?.token_purchase !== undefined && cached?.token_purchase !== null) return cached.token_purchase;
                            if (cached?.tokenPurchased !== undefined && cached?.tokenPurchased !== null) return cached.tokenPurchased;
                          } catch (_e) { }
                          if (tokenPlan?.tokens !== undefined && tokenPlan?.tokens !== null) return tokenPlan.tokens;
                          return null;
                        })();

                        return (
                          <TokenCard
                            tokenBalance={tokenBalance}
                            tokenLimit={tokenLimit}
                            onPurchaseTokens={handlePurchaseTokens}
                            isPurchasingTokens={purchasingTokens}
                            isTokenLoading={isTokenLoading}
                            t={t}
                            language={language}
                            tokenPurchaseCount={tokenPurchaseCount}
                            tokenPlanPrice={tokenPlanPrice}
                          />
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}

              {/* Language toggle - below the login card (mobile/tablet only; desktop uses the bottom-center toggle) */}
              <div className="relative z-10 flex w-full justify-center flex-shrink-0 mt-4 lg:hidden">
                <div className="flex rounded-[22.5px] shadow-[inset_0_0_0_3px_#904DB0] h-[42px] bg-[#F8F7FC] min-w-[160px] overflow-hidden">
                  <button
                    onClick={() => switchLanguage("es")}
                    className={`flex-1 h-full text-[14px] uppercase flex items-center justify-center px-5 whitespace-nowrap transition-colors font-roboto leading-none tracking-normal rounded-r-full ${language === "es"
                      ? "bg-[#904DB0] text-[#F8F7FC] font-black"
                      : "bg-transparent text-[#904DB0] font-semibold"
                      }`}
                  >
                    ESP
                  </button>

                  <button
                    onClick={() => switchLanguage("en")}
                    className={`flex-1 h-full text-[14px] uppercase flex items-center justify-center px-5 whitespace-nowrap transition-colors font-roboto leading-none tracking-normal rounded-l-full ${language === "en"
                      ? "bg-[#904DB0] text-[#F8F7FC] font-black"
                      : "bg-transparent text-[#904DB0] font-semibold"
                      }`}
                  >
                    ENG
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* LEFT SIDE */}
          <div className="w-full lg:w-1/2 lg:order-1 flex flex-col justify-between relative bg-[#F7F8FD] px-4 sm:px-6 lg:px-6 xl:px-10 2xl:px-12 py-4 sm:py-6 lg:pt-6 lg:pb-3 xl:pt-6 xl:pb-3.5 2xl:py-8 lg:h-full overflow-hidden auth-outer-col-left">
            {/* Top header area for Logo and Back to App button */}
            <div className={`w-full ${showBackToApp ? "h-auto mb-2 lg:mb-2.5" : "h-[36px] sm:h-[48px] lg:h-[40px] xl:h-[44px] 2xl:h-[66px]"} flex items-center justify-between shrink-0 z-20 auth-header ${showBackToApp ? "auth-header-with-back" : ""}`}>
              <div className="flex flex-col items-start gap-1.5 sm:gap-2">
                <img
                  src={Logo}
                  alt="Logo"
                  width="840"
                  height="348"
                  loading="eager"
                  decoding="sync"
                  className="w-[110px] sm:w-[150px] lg:w-[135px] xl:w-[150px] 2xl:w-[230px] h-auto object-contain auth-logo"
                  style={{ aspectRatio: "840/348" }}
                />
                {showBackToApp && (
                  <button
                    onClick={() => navigate("/chat")}
                    className="hidden lg:flex items-center gap-1.5 sm:gap-2 h-[26px] lg:h-[28px] xl:h-[30px] 2xl:h-[34px] px-3 lg:px-3.5 xl:px-4 2xl:px-5 rounded-[22.5px] bg-[#904DB0] text-[#F8F7FC] text-[10.5px] lg:text-[11px] xl:text-[11.5px] 2xl:text-[13px] font-black uppercase whitespace-nowrap shadow-[inset_0_0_0_3px_#904DB0] transition-colors hover:bg-[#7d3fa0] cursor-pointer"
                  >
                    <IoArrowBack className="w-3.5 h-3.5" />
                    {t.backToApp || "Back to App"}
                  </button>
                )}
              </div>
            </div>

            {/* Main content vertically centered in remaining space */}
            <div
              ref={leftWrapperRef}
              className={`w-full flex-1 flex flex-col items-center ${activeTab === "plans"
                ? "lg:justify-start justify-center"
                : "justify-center"
                } py-1 sm:py-2 ${activeTab === "plans"
                  ? "lg:pb-2 xl:pb-2 2xl:pb-4"
                  : "lg:pb-14 xl:pb-14 2xl:pb-4"
                } auth-content-wrapper`}
              style={
                activeTab === "plans" && rightPlansTopOffset > 0
                  ? { paddingTop: `${rightPlansTopOffset}px` }
                  : undefined
              }
            >
              {activeTab === "login" ? (
                <div className="w-full flex flex-col justify-center gap-4 sm:gap-6 lg:gap-5 xl:gap-6 2xl:gap-8 max-w-[480px] xl:max-w-[520px] 2xl:max-w-[600px] mx-auto z-10 pt-1.5 sm:pt-4 lg:pt-0 pb-1.5 sm:pb-4 lg:pb-0 auth-container">
                  <div className="w-full flex justify-center">
                    <div className="w-full max-w-[480px] xl:max-w-[520px] 2xl:max-w-[600px] flex flex-col items-start">

                      <h1
                        className={`text-left text-[#1A0E3E] leading-[1.15] font-light w-full ${language === "es"
                          ? "text-[22px] sm:text-[30px] lg:text-[31px] xl:text-[32px] 2xl:text-[44px] auth-left-title-es"
                          : "text-[24px] sm:text-[32px] lg:text-[34px] xl:text-[35px] 2xl:text-[48px] auth-left-title"
                          }`}
                      >
                        {t.title1}
                        <br />
                        <span className="font-bold text-[#3D2B90]">
                          {t.title2}
                        </span>
                      </h1>

                      <p className="text-left text-[#3D2B90] mt-2 sm:mt-3 lg:mt-3 xl:mt-3 2xl:mt-4 text-[13.5px] sm:text-[16px] lg:text-[15.5px] xl:text-[16px] 2xl:text-[20px] leading-[1.3] sm:leading-[1.35] lg:leading-[1.4] xl:leading-[1.4] 2xl:leading-[1.4] font-light w-full auth-left-subtitle">
                        {t.subtitle1}
                        <br />
                        {t.subtitle2}
                        <br />
                        {t.subtitle3}
                      </p>

                      <button
                        onClick={() => navigate("/sign-up")}
                        className={`w-full h-[38px] sm:h-[44px] lg:h-[42px] xl:h-[44px] 2xl:h-[54px] mt-6 sm:mt-8 lg:mt-11 xl:mt-12 2xl:mt-14 bg-[#1A0E3E] text-[#F7F7FF] rounded-[87.5px] font-bold text-[13px] sm:text-[14.5px] lg:text-[14.5px] xl:text-[15px] 2xl:text-[17.5px] leading-none uppercase hover:bg-[#281561] transition-colors flex items-center justify-center auth-trial-btn ${isLoggedIn ? "hidden" : ""
                          }`}
                      >
                        {t.trialBtn}
                      </button>

                    </div>
                  </div>

                  {/* 3 Cards */}
                  <div className="flex items-stretch justify-center gap-2 sm:gap-2.5 lg:gap-2.5 xl:gap-3 2xl:gap-4 mt-2 sm:mt-4 lg:mt-2.5 xl:mt-3 2xl:mt-5 w-full max-w-[700px] auth-feature-cards">
                    {[
                      {
                        icon: PrivateIcon,
                        alt: "Private",
                        title: t.card1Title,
                        sub: t.card1Sub,
                      },
                      {
                        icon: LinkoappIcon,
                        alt: "Data Anonymization",
                        title: t.card2Title,
                        sub: t.card2Sub,
                      },
                      {
                        icon: ModalIcon,
                        alt: "No Model",
                        title: t.card3Title,
                        sub: t.card3Sub,
                      },
                    ].map(({ icon, alt, title, sub }) => (
                      <div
                        key={alt}
                        className="flex flex-col items-center justify-center gap-0.5 sm:gap-1 lg:gap-0.5 xl:gap-1 2xl:gap-2 bg-[#FFFFFF] rounded-[12px] sm:rounded-[24px] shadow-[0px_0px_23.6px_-11px_#C7BFE4] flex-1 h-[78px] sm:h-[105px] lg:h-[88px] xl:h-[92px] 2xl:h-[145px] px-1 sm:px-2.5 lg:px-2 xl:px-2.5 2xl:px-3 py-1 sm:py-2 lg:py-1.5 xl:py-2 2xl:py-3 min-w-0 overflow-hidden auth-feature-card"
                      >
                        <img
                          src={icon}
                          alt={alt}
                          className="h-[22px] sm:h-[30px] lg:h-[26px] xl:h-[28px] 2xl:h-[52px] w-[22px] sm:w-[30px] lg:w-[26px] xl:w-[28px] 2xl:w-[52px] object-contain shrink-0"
                        />
                        <div className="flex flex-col items-center text-center min-w-0">
                          <span className="text-[#1A0E3E] font-bold text-[9.5px] sm:text-[11.5px] lg:text-[11px] xl:text-[11.5px] 2xl:text-[17px] leading-tight auth-feature-card-title">
                            {title}
                          </span>
                          <span className="text-[#1A0E3E] font-normal text-[8.5px] sm:text-[10px] lg:text-[9.5px] xl:text-[10px] 2xl:text-[15px] leading-tight mt-0.5 auth-feature-card-sub">
                            {sub}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="w-full flex flex-col justify-start max-w-[680px] 2xl:max-w-[760px] mx-auto z-10 px-2 sm:px-4 py-0 auth-plans-container">
                  <p className="text-[#1A0E3E] text-[26px] sm:text-[30px] lg:text-[36px] xl:text-[40px] 2xl:text-[48px] font-light leading-[1.15] text-left w-full auth-plans-left-title">
                    {t.plansSubtitle}
                    <br />
                    <span className="font-bold text-[#3D2B90]">
                      {t.plansSubtitleBold1 || "consistency, accuracy,"}
                      <br />
                      {t.plansSubtitleBold2 || "and control."}
                    </span>
                  </p>
                  <ul className="flex flex-col gap-3.5 sm:gap-4 lg:gap-4 xl:gap-4.5 2xl:gap-5.5 mt-4 sm:mt-5 lg:mt-5 xl:mt-5.5 2xl:mt-7 w-full auth-plans-bullets">
                    {t.plansBullets.map((b, i) => {
                      const planLeftIcons = [
                        <svg key="pl0" className="w-4.5 h-4.5 sm:w-5 sm:h-5 lg:w-5 lg:h-5 xl:w-5.5 xl:h-5.5 2xl:w-6.5 2xl:h-6.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>,
                        <svg key="pl1" className="w-4.5 h-4.5 sm:w-5 sm:h-5 lg:w-5 lg:h-5 xl:w-5.5 xl:h-5.5 2xl:w-6.5 2xl:h-6.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
                        <svg key="pl2" className="w-4.5 h-4.5 sm:w-5 sm:h-5 lg:w-5 lg:h-5 xl:w-5.5 xl:h-5.5 2xl:w-6.5 2xl:h-6.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>,
                        <svg key="pl3" className="w-4.5 h-4.5 sm:w-5 sm:h-5 lg:w-5 lg:h-5 xl:w-5.5 xl:h-5.5 2xl:w-6.5 2xl:h-6.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>,
                      ];
                      return (
                        <li
                          key={i}
                          className="flex items-center gap-3.5 sm:gap-4 lg:gap-4 xl:gap-4.5 2xl:gap-5 text-[#3D2B90] text-[15.5px] sm:text-[17px] lg:text-[17.5px] xl:text-[19px] 2xl:text-[22px] font-normal leading-snug text-left auth-plans-bullet-item"
                        >
                          <span className="w-8 h-8 sm:w-8.5 sm:h-8.5 lg:w-9 lg:h-9 xl:w-10 xl:h-10 2xl:w-12 2xl:h-12 rounded-full bg-[#3D2B90]/15 border border-[#3D2B90] text-[#3D2B90] flex items-center justify-center shrink-0 auth-plans-bullet-icon">
                            {planLeftIcons[i % planLeftIcons.length]}
                          </span>
                          <span>{b}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Login;